const sections = [
  {
    id: "information-we-collect",
    title: "Information We Collect",
    content: (
      <>
        <p className="text-[#667085] leading-7">We may collect the following information:</p>
        <ul className="mt-4 grid gap-2">
          {[
            "Name",
            "Mobile phone number",
            "WhatsApp phone number",
            "Email address (if voluntarily provided)",
            "Messages sent through our WhatsApp service",
            "Media shared during conversations, including images, documents, audio, and videos",
            "Device information such as browser type, IP address, operating system, and usage logs",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#6d4aff]" />
              {item}
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: "how-we-collect",
    title: "How We Collect Information",
    content: (
      <>
        <p className="text-[#667085] leading-7">Information is collected through several channels:</p>
        <ul className="mt-4 grid gap-2">
          {[
            "Directly from you when you communicate with a business via WhatsApp.",
            "From businesses that use DocLift to deliver customer support or other services to their customers.",
            "From Meta through the WhatsApp Business Platform, including APIs, Embedded Signup flows and webhook events.",
            "Automatically when you access DocLift websites or services, through server logs, cookies and similar tracking technologies.",
            "From third-party integrations authorised by the connected business, such as appointment systems, CRM platforms, calendars or customer-support tools.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#13a66f]" />
              {item}
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: "business-and-end-users",
    title: "Business Customers and End Users",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          DocLift serves two distinct groups: businesses that integrate DocLift into their operations, and the
          end users those businesses communicate with through WhatsApp.
        </p>
        <p className="mt-4 text-[#667085] leading-7">
          For personal information processed on behalf of a connected business — such as WhatsApp conversations
          and customer records — the business generally acts as the data controller, determining the purpose and
          means of processing. DocLift acts as a service provider or data processor, handling that information
          solely on the business's instructions.
        </p>
        <p className="mt-4 text-[#667085] leading-7">
          DocLift acts as the data controller for information used to administer platform accounts, manage
          subscriptions, maintain security, communicate with business customers, and fulfil legal obligations.
        </p>
      </>
    ),
  },
  {
    id: "international-data",
    title: "International Data Processing",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          DocLift and its authorised service providers operate globally. Your information may be processed in
          countries other than the one in which you or the connected business is located, and those countries
          may have different data-protection standards.
        </p>
        <p className="mt-4 text-[#667085] leading-7">
          Where required by applicable law, we apply appropriate contractual, technical and organisational
          safeguards — such as standard contractual clauses — to protect personal information transferred
          across borders.
        </p>
      </>
    ),
  },
  {
    id: "ai-processing",
    title: "AI and Automated Processing",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          DocLift uses artificial intelligence and automated processing to power customer-support and
          business-automation features. When you message a business through WhatsApp, DocLift may automatically:
        </p>
        <ul className="mt-4 grid gap-2">
          {[
            "Analyse your message and retrieve relevant information from the business's authorised knowledge base.",
            "Generate or recommend responses on behalf of the business.",
            "Classify your request and route the conversation to the appropriate team or workflow.",
            "Assist with tasks such as appointment booking, confirmations and general customer support.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#6d4aff]" />
              {item}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-[#667085] leading-7">
          Message content may be securely processed by authorised AI, cloud infrastructure and automation
          providers strictly for delivering and maintaining the requested services. AI-generated responses
          may be reviewed, corrected or overridden by authorised representatives of the connected business.
        </p>
        <p className="mt-4 text-[#667085] leading-7">
          When a business connects its WhatsApp Business Account to DocLift, we may receive and process
          associated account and messaging metadata, including business identifiers, phone number IDs,
          message template details, message delivery and read status, conversation event metadata, and
          information about authorised business assets and permissions.
        </p>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "How We Use Your Information",
    content: (
      <>
        <p className="text-[#667085] leading-7">We use your information to:</p>
        <ul className="mt-4 grid gap-2">
          {[
            "Provide and improve our services.",
            "Respond to your messages and customer support requests.",
            "Deliver the services you request.",
            "Improve the functionality and performance of DocLift.",
            "Maintain the security and integrity of our platform.",
            "Detect, investigate, and prevent fraud, abuse, or unauthorized activities.",
            "Comply with applicable legal and regulatory obligations.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#13a66f]" />
              {item}
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: "whatsapp",
    title: "WhatsApp Business Platform",
    content: (
      <p className="text-[#667085] leading-7">
        DocLift uses the official WhatsApp Business Platform provided by Meta to communicate with users. Messages
        exchanged through WhatsApp may be processed solely for the purpose of providing our services. We only
        collect and process the information necessary to deliver the requested functionality and do not sell, rent,
        or use your WhatsApp conversations for advertising or marketing purposes without your consent.
      </p>
    ),
  },
  {
    id: "third-party-sharing",
    title: "Third-Party Sharing",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          We may share information with service providers that assist us in delivering DocLift, including:
        </p>
        <ul className="mt-4 grid gap-2">
          {[
            "Meta and the WhatsApp Business Platform",
            "Cloud hosting, storage and database providers",
            "Artificial intelligence and language-model providers",
            "Workflow automation providers",
            "Authentication and identity-management providers",
            "Security, logging and monitoring providers",
            "Analytics providers",
            "Email and notification providers",
            "Calendar, CRM, appointment and business-software integrations authorised by the connected business",
            "Professional advisers, regulators or authorities where legally required",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#6d4aff]" />
              {item}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-[#667085] leading-7">
          These providers are permitted to use your information only to the extent necessary to perform services on
          our behalf and are required to maintain appropriate confidentiality and security standards.
        </p>
        <p className="mt-3 text-sm font-semibold text-[#374151]">
          We do <span className="text-[#6d4aff]">not</span> sell or trade your personal information to third parties.
        </p>
      </>
    ),
  },
  {
    id: "data-security",
    title: "Data Security",
    content: (
      <p className="text-[#667085] leading-7">
        We implement appropriate technical and organizational measures to protect your personal information against
        unauthorized access, alteration, disclosure, or destruction. Although we strive to use commercially accepted
        security practices, no method of electronic transmission or storage is completely secure, and we cannot
        guarantee absolute security.
      </p>
    ),
  },
  {
    id: "data-retention",
    title: "Data Retention",
    content: (
      <>
        <p className="text-[#667085] leading-7">We retain your information only for as long as necessary to:</p>
        <ul className="mt-4 grid gap-2">
          {[
            "Provide our services.",
            "Fulfill contractual obligations.",
            "Comply with applicable laws and regulations.",
            "Resolve disputes.",
            "Enforce our legal agreements.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#13a66f]" />
              {item}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-[#667085] leading-7">
          Retention periods may vary depending on the type of information, the settings selected by the connected
          business, contractual requirements, security requirements, and applicable law.
        </p>
        <p className="mt-4 text-[#667085] leading-7">
          WhatsApp conversations and related metadata may be retained for the period configured by the connected
          business or for as long as necessary to provide the service.
        </p>
        <p className="mt-4 text-[#667085] leading-7">
          When a business terminates its use of DocLift, associated information is deleted or anonymised in
          accordance with the applicable agreement and retention schedule, except where continued retention is
          legally required.
        </p>
        <p className="mt-4 text-sm text-[#667085]">
          Information that is no longer required is securely deleted or anonymised.
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "Your Rights",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          Depending on your jurisdiction and applicable privacy laws, you may have the right to:
        </p>
        <ul className="mt-4 grid gap-2">
          {[
            "Access your personal information.",
            "Correct inaccurate or incomplete information.",
            "Request deletion of your personal information.",
            "Withdraw consent where processing is based on consent.",
            "Request a copy of your personal data.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#6d4aff]" />
              {item}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-[#667085]">
          To exercise any of these rights, please contact us using the details provided below.
        </p>
      </>
    ),
  },
  {
    id: "data-deletion",
    title: "Data Deletion Request",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          You may request access to or deletion of personal information processed by DocLift by emailing{" "}
          <a href="mailto:info@doclift.ai" className="font-semibold text-[#6d4aff] hover:underline">info@doclift.ai</a>{" "}
          with the subject line <span className="font-semibold text-[#374151]">&ldquo;DocLift Data Deletion Request.&rdquo;</span>
        </p>
        <p className="mt-4 text-[#667085] leading-7">Please include, where applicable:</p>
        <ul className="mt-3 grid gap-2">
          {[
            "Your full name",
            "Your WhatsApp phone number, including country code",
            "The name of the business you contacted through WhatsApp",
            "A description of the information you want deleted",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#6d4aff]" />
              {item}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-[#667085] leading-7">
          We may request additional information to verify your identity and prevent unauthorised deletion requests.
        </p>
        <p className="mt-4 text-[#667085] leading-7">
          When DocLift processes information on behalf of a business, we may forward your request to that business
          or assist the business in responding to it.
        </p>
        <p className="mt-4 text-[#667085] leading-7">
          Following verification, we will delete or anonymise the relevant information unless retention is required
          for legal, regulatory, security, fraud-prevention or legitimate contractual purposes. We will communicate
          the outcome of the request using the contact information provided.
        </p>
      </>
    ),
  },
  {
    id: "childrens-privacy",
    title: "Children's Privacy",
    content: (
      <p className="text-[#667085] leading-7">
        DocLift is not intended for use by children under the age of 13 (or the minimum age required under
        applicable law). We do not knowingly collect personal information from children.
      </p>
    ),
  },
  {
    id: "changes",
    title: "Changes to This Privacy Policy",
    content: (
      <p className="text-[#667085] leading-7">
        We may update this Privacy Policy from time to time to reflect changes in our services, legal requirements,
        or business practices. Any updates will be posted on this page with the revised "Last Updated" date.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact Us",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          If you have any questions about this Privacy Policy or wish to exercise your privacy rights, please
          contact us:
        </p>
        <div className="mt-5 grid gap-2 rounded-2xl border border-[rgba(109,74,255,0.12)] bg-white/70 p-5 text-sm backdrop-blur-sm">
          {[
            ["Company", "Orryworx Pvt. Ltd."],
            ["Application", "DocLift"],
          ].map(([label, value]) => (
            <div key={label} className="flex gap-2">
              <span className="w-24 font-semibold text-[#374151]">{label}</span>
              <span className="text-[#667085]">{value}</span>
            </div>
          ))}
          <div className="flex gap-2">
            <span className="w-24 font-semibold text-[#374151]">Email</span>
            <a href="mailto:info@doclift.ai" className="text-[#6d4aff] hover:underline">
              info@doclift.ai
            </a>
          </div>
          <div className="flex gap-2">
            <span className="w-24 font-semibold text-[#374151]">Website</span>
            <a href="http://www.orryworx.com" target="_blank" rel="noreferrer" className="text-[#6d4aff] hover:underline">
              www.orryworx.com
            </a>
          </div>
        </div>
      </>
    ),
  },
];

const PrivacyPolicy = () => {
  return (
    <div
      className="min-h-screen"
      style={{
        background:
          "radial-gradient(circle at 86% 18%, rgba(109,74,255,0.08), transparent 26%), radial-gradient(circle at 16% 78%, rgba(19,166,111,0.07), transparent 28%), linear-gradient(180deg,#ffffff 0%,#fbfaff 52%,#f8fff9 100%)",
      }}
    >
      {/* Hero banner */}
      <div className="border-b border-[rgba(109,74,255,0.1)] bg-white/60 backdrop-blur-sm">
        <div className="mx-auto max-w-[1100px] px-5 py-14 sm:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(109,74,255,0.18)] bg-[rgba(109,74,255,0.07)] px-4 py-1.5 text-xs font-bold text-[#6d4aff]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#6d4aff]" />
            Legal
          </span>
          <h1 className="mt-5 text-4xl font-black tracking-tight text-[#101522] sm:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm text-[#667085]">
            Last Updated: <span className="font-semibold text-[#374151]">July 10, 2026</span>
          </p>
          <p className="mt-5 max-w-2xl text-base leading-7 text-[#667085]">
            Welcome to <span className="font-semibold text-[#374151]">DocLift</span>, a product developed and
            operated by <span className="font-semibold text-[#374151]">Orryworx Pvt. Ltd.</span> We value your
            privacy and are committed to protecting your personal information. This Privacy Policy explains how we
            collect, use, store, and protect your information when you interact with DocLift through WhatsApp and
            our related services.
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-[1100px] px-5 py-12 sm:px-8 lg:grid lg:grid-cols-[220px_1fr] lg:gap-12">
        {/* Sticky TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl border border-[rgba(109,74,255,0.1)] bg-white/70 p-5 backdrop-blur-sm">
            <p className="mb-3 text-xs font-bold uppercase tracking-widest text-[#6d4aff]">Contents</p>
            <nav className="grid gap-1">
              {sections.map((s, i) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-semibold text-[#667085] transition hover:bg-[rgba(109,74,255,0.06)] hover:text-[#6d4aff]"
                >
                  <span className="text-[10px] font-bold text-[#6d4aff] opacity-60">{String(i + 1).padStart(2, "0")}</span>
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Sections */}
        <main className="grid gap-6">
          {sections.map((s, i) => (
            <section
              key={s.id}
              id={s.id}
              className="rounded-2xl border border-[rgba(109,74,255,0.1)] bg-white/70 p-6 backdrop-blur-sm transition hover:border-[rgba(109,74,255,0.2)] hover:shadow-[0_18px_52px_rgba(82,63,181,0.08)] sm:p-8"
            >
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[rgba(109,74,255,0.08)] text-xs font-black text-[#6d4aff]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="text-lg font-black text-[#101522]">{s.title}</h2>
              </div>
              {s.content}
            </section>
          ))}
        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
